import React, { createContext, useContext, useEffect, useState } from "react";
import {
  HttpTransportType,
  HubConnectionBuilder,
  LogLevel,
} from "@microsoft/signalr";
import { notification } from "antd";

import {
  getAccessToken,
  getSessionData,
} from "../../utils/interceptors/localStorageService";
import { getWebSocketUrl } from "../config/tenantConfiguration";
import { saveNotificationsToSession } from "../../utils/helper/util";
import { useNotification } from "../../store/context/notification-context";

const WebSocketContext = createContext(null);

// Navigation helper - stores navigate function from React Router
let navigationRef = null;

export const setNavigationRef = (navigate) => {
  navigationRef = navigate;
};

const navigateTo = (path) => {
  if (navigationRef) {
    navigationRef(path);
  } else {
    console.warn("Navigation not available yet. Redirecting via window.location");
    window.location.href = path;
  }
};

const URL = getWebSocketUrl(); 

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error("useWebSocket must be used within a WebSocketProvider");
  }
  return context;
};

export const WebSocketProvider = ({ children }) => {
  const { setSignalRNotification } = useNotification();
  // Initialize with null and then update after checking session
  const [connection, setConnection] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [userId, setUserId] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize access token
  useEffect(() => {
    const initializeToken = () => {
      const sessionData = getSessionData();
      if (sessionData?.accessToken) {
        setAccessToken(sessionData.accessToken);
      }
      if (sessionData?.accessToken) {
        setUserId(sessionData.userId);
      }
    };

    initializeToken();
    setIsInitialized(true);
  }, []);

  // ========================================
  // ENHANCED MESSAGE HANDLERS WITH BACKWARD COMPATIBILITY
  // ========================================

  /**
   * Handle ReceiveNotification - New standardized format
   * Supports: General notifications, system alerts, approval requests
   */
  const handleNotification = (notification) => {
    console.log("📬 ReceiveNotification:", notification);

    // Save to state and session
    setSignalRNotification(notification);
    saveNotificationsToSession(userId, {
      ...notification,
      id: notification.notificationId || Date.now(),
      timestamp: notification.timestamp || new Date().toISOString(),
    });

    // Determine notification style based on priority
    const notificationConfig = {
      message: notification.title || "Notification",
      description: notification.message,
      placement: "topRight",
      duration: notification.priority === "critical" ? 0 : 4.5, // Critical stays until closed
    };

    // Handle notification types with specific behaviors
    switch (notification.notificationType?.toLowerCase()) {
      case "transaction":
        handleTransactionNotification(notification, notificationConfig);
        break;
      
      case "loan":
        handleLoanNotification(notification, notificationConfig);
        break;
      
      case "approval":
        handleApprovalNotification(notification, notificationConfig);
        break;
      
      case "system":
        handleSystemNotification(notification, notificationConfig);
        break;
      
      case "alert":
        handleAlertNotification(notification, notificationConfig);
        break;
      
      case "account":
        handleAccountNotification(notification, notificationConfig);
        break;
      
      default:
        handleGenericNotification(notification, notificationConfig);
    }
  };

  /**
   * Handle ReceiveTransactionStatus - Backward compatible with old format
   * Supports: Legacy transaction messages and new formats
   */
  const handleTransactionStatus = (messageRes) => {
    console.log("💳 ReceiveTransactionStatus:", messageRes);

    // Extract fields (backward compatible with old format)
    const {
      messageType,
      status,
      action,
      loanRequestId,
      transactionId,
      message,
      Message, // Uppercase M for backward compatibility
      requestId,
      type,
      IsSuccess,
    } = messageRes;

    // Use Message or message (backward compatible)
    const messageText = message || Message || "Transaction update";

    // Save to state and session
    setSignalRNotification(messageRes);
    saveNotificationsToSession(userId, {
      ...messageRes,
      id: Date.now(),
      timestamp: new Date().toISOString(),
    });

    // Handle different action types
    const actionLower = action?.toLowerCase();

    if (actionLower === "initiated") {
      notification.info({
        message: "Transaction Initiated",
        description: messageText,
        placement: "topRight",
      });
    } 
    else if (actionLower === "transactionupdate") {
      if (status === "success") {
        notification.success({
          message: "Transaction Successful",
          description: messageText,
          placement: "topRight",
        });
      } else if (status === "failed") {
        notification.error({
          message: "Transaction Failed",
          description: messageText,
          placement: "topRight",
        });
      } else {
        notification.info({
          message: `Transaction ${status || "Update"}`,
          description: messageText,
          placement: "topRight",
        });
      }
    } 
    else if (actionLower === "approvalrequired") {
      notification.info({
        message: "Approval Required",
        description: `A loan request requires your approval. Loan Request ID: ${loanRequestId}`,
        placement: "topRight",
        btn: (
          <button
            onClick={() => {
              console.log(`Viewing Loan Request ID: ${loanRequestId}`);
              navigateTo(`/loan-requests/${loanRequestId}`);
              notification.destroy();
            }}
            className="px-4 py-2 text-sm text-blue-600 hover:text-blue-800"
          >
            View Loan Request
          </button>
        ),
      });
    }
    // Backward compatibility: Handle old "transaction_update" action
    else if (actionLower === "transaction_update") {
      if (IsSuccess === true) {
        notification.success({
          message: "Transaction Processing",
          description: messageText,
          placement: "topRight",
        });
      } else if (IsSuccess === false) {
        notification.error({
          message: "Transaction Failed",
          description: messageText,
          placement: "topRight",
        });
      } else {
        notification.info({
          message: "Transaction Update",
          description: messageText,
          placement: "topRight",
        });
      }
    }
    // Fallback for unknown actions
    else {
      notification.info({
        message: "Transaction Update",
        description: messageText,
        placement: "topRight",
      });
    }
  };

  /**
   * Handle ReceiveApprovalNotification - Simple approval messages
   */
  const handleApprovalMessage = (approvalMessage) => {
    console.log("✅ ReceiveApprovalNotification:", approvalMessage);

    // Save to state and session
    setSignalRNotification(approvalMessage);
    saveNotificationsToSession(userId, {
      ...approvalMessage,
      id: Date.now(),
      timestamp: new Date().toISOString(),
    });

    notification.warning({
      message: "Approval Required",
      description: approvalMessage.Message || approvalMessage.message,
      placement: "topRight",
      duration: 0, // Stay until dismissed
      btn: (
        <button
          onClick={() => {
            console.log(`Review Approval Request ID: ${approvalMessage.requestId}`);
            navigateTo(`/approvals/${approvalMessage.requestId}`);
            notification.destroy();
          }}
          className="px-4 py-2 text-sm text-orange-600 hover:text-orange-800 font-medium"
        >
          Review Request
        </button>
      ),
    });
  };

  /**
   * Handle ReceiveGroupNotification - Group messages
   */
  const handleGroupMessage = (groupMessage) => {
    console.log("👥 ReceiveGroupNotification:", groupMessage);

    // Save to state and session
    setSignalRNotification(groupMessage);
    saveNotificationsToSession(userId, {
      ...groupMessage,
      id: Date.now(),
      timestamp: new Date().toISOString(),
    });

    notification.info({
      message: "Group Notification",
      description: groupMessage.Message || groupMessage.message,
      placement: "topRight",
    });
  };

  // ========================================
  // NOTIFICATION TYPE HANDLERS
  // ========================================

  const handleTransactionNotification = (notification, config) => {
    const { data, action, priority } = notification;

    // Add action button if available
    if (action === "view_transaction" && data?.transactionId) {
      config.btn = (
        <button
          onClick={() => {
            console.log(`Navigate to transaction: ${data.transactionId}`);
            navigateTo(`/transactions/${data.transactionId}`);
            notification.destroy(); // Close notification after click
          }}
          className="px-4 py-2 text-sm text-blue-600 hover:text-blue-800 font-medium"
        >
          View Transaction
        </button>
      );
    }

    // Show notification based on priority
    if (priority === "critical" || priority === "high") {
      notification.warning(config);
    } else {
      notification.info(config);
    }
  };

  const handleLoanNotification = (notif, config) => {
    const { data, action, priority } = notif;

    // Add action button for loan details
    if (action === "view_loan" && data?.loanId) {
      config.btn = (
        <button
          onClick={() => {
            console.log(`Navigate to loan: ${data.loanId}`);
            navigateTo(`/loan-requests/${data.loanId}`);
            notification.destroy(); // Close notification after click
          }}
          className="px-4 py-2 text-sm text-green-600 hover:text-green-800 font-medium"
        >
          View Loan Details
        </button>
      );
    } else if (action === "make_payment" && data?.loanId) {
      config.btn = (
        <button
          onClick={() => {
            console.log(`Make payment for loan: ${data.loanId}`);
            navigateTo(`/loans/${data.loanId}/payment`);
            notification.destroy(); // Close notification after click
          }}
          className="px-4 py-2 text-sm text-green-600 hover:text-green-800 font-medium"
        >
          Make Payment
        </button>
      );
    }

    // Show notification
    if (priority === "high" || priority === "critical") {
      notification.success(config);
    } else {
      notification.info(config);
    }
  };

  const handleApprovalNotification = (notif, config) => {
    const { data, action } = notif;

    config.duration = 0; // Stay until dismissed

    // Add action buttons
    if (action === "view_approval" || action === "approve_request") {
      config.btn = (
        <div className="flex gap-2">
          <button
            onClick={() => {
              console.log(`Review approval: ${data?.approvalId || notif.notificationId}`);
              navigateTo(`/approvals/${data?.approvalId || data?.requestId || notif.notificationId}`);
              notification.destroy(); // Close notification after click
            }}
            className="px-4 py-2 text-sm text-blue-600 hover:text-blue-800 font-medium"
          >
            Review
          </button>
        </div>
      );
    }

    notification.warning(config);
  };

  const handleSystemNotification = (notif, config) => {
    const { priority, action, data } = notif;

    // Add action button if specified
    if (action === "view_details" && data?.detailsUrl) {
      config.btn = (
        <button
          onClick={() => {
            console.log(`View system details`);
            navigateTo(data.detailsUrl);
            notification.destroy();
          }}
          className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 font-medium"
        >
          View Details
        </button>
      );
    }

    if (priority === "critical") {
      config.duration = 0; // Critical system messages stay
      notification.error(config);
    } else if (priority === "high") {
      notification.warning(config);
    } else {
      notification.info(config);
    }
  };

  const handleAlertNotification = (notif, config) => {
    const { data, action } = notif;

    config.duration = 0; // Alerts stay until acknowledged

    if (action === "acknowledge") {
      config.btn = (
        <button
          onClick={() => {
            console.log(`Acknowledge alert: ${notif.notificationId}`);
            // You can call an API to mark as acknowledged here
            notification.destroy();
          }}
          className="px-4 py-2 text-sm text-red-600 hover:text-red-800 font-medium"
        >
          Acknowledge
        </button>
      );
    } else if (action === "view_details" && data?.detailsUrl) {
      config.btn = (
        <button
          onClick={() => {
            console.log(`View alert details`);
            navigateTo(data.detailsUrl);
            notification.destroy();
          }}
          className="px-4 py-2 text-sm text-red-600 hover:text-red-800 font-medium"
        >
          View Details
        </button>
      );
    }

    notification.error(config);
  };

  const handleAccountNotification = (notif, config) => {
    const { action, data } = notif;

    if (action === "view_account") {
      config.btn = (
        <button
          onClick={() => {
            console.log(`Navigate to account`);
            navigateTo('/account');
            notification.destroy();
          }}
          className="px-4 py-2 text-sm text-blue-600 hover:text-blue-800 font-medium"
        >
          View Account
        </button>
      );
    } else if (action === "update_profile") {
      config.btn = (
        <button
          onClick={() => {
            console.log(`Navigate to profile`);
            navigateTo('/settings/profile');
            notification.destroy();
          }}
          className="px-4 py-2 text-sm text-blue-600 hover:text-blue-800 font-medium"
        >
          Update Profile
        </button>
      );
    }

    notification.info(config);
  };

  const handleGenericNotification = (notif, config) => {
    // Generic handler for unknown types
    notification.info(config);
  };

  const startConnection = async () => {
    // Don't attempt connection if there's no access token
    if (!accessToken) {
      // console.log('No access token available, skipping connection');
      return;
    }

    try {
      if (connection) {
        await connection.stop();
      }

      const newConnection = new HubConnectionBuilder()
        .withUrl(URL, {
          accessTokenFactory: () => accessToken,
          transport:
            HttpTransportType.WebSockets | HttpTransportType.LongPolling,
        })
        .configureLogging(LogLevel.Information)
        .build();

      newConnection.on("ReceiveNotification", handleNotification);
      newConnection.on("ReceiveTransactionStatus", handleTransactionStatus);
      newConnection.on("ReceiveApprovalNotification", handleApprovalMessage);
      newConnection.on("ReceiveGroupNotification", handleGroupMessage);

      await newConnection.start();
      // console.log('SignalR connection established');
      setConnection(newConnection);
    } catch (error) {
      console.error("SignalR Connection Error:", error);
      // Only retry if we still have an access token
      if (accessToken) {
        setTimeout(startConnection, 5000);
      }
    }
  };

  // Effect to monitor access token changes
  useEffect(() => {
    const checkAccessToken = () => {
      const sessionData = getSessionData();
      const currentToken = sessionData?.accessToken || null;
      const currentUserId = sessionData?.userId || null;

      if (currentToken !== accessToken) {
        setAccessToken(currentToken);
      }
      if (currentUserId !== userId) {
        setUserId(currentUserId);
      }
    };

    // Check for token changes every minute
    const tokenCheckInterval = setInterval(checkAccessToken, 60000);

    return () => {
      clearInterval(tokenCheckInterval);
    };
  }, [accessToken, userId]);

  // Effect to manage connection based on access token
  useEffect(() => {
    if (isInitialized && accessToken && userId) {
      startConnection();
    } else if (!accessToken && connection) {
      // Disconnect if token is removed
      connection.stop();
      setConnection(null);
    }

    return () => {
      if (connection) {
        connection.stop();
      }
    };
  }, [accessToken, userId, isInitialized]);

  const value = {
    isConnected: connection?.state === "Connected",
    connection,
    isInitialized,
    hasToken: !!accessToken,
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};
